import { FlyToInterpolator, Marker, WebMercatorViewport } from "react-map-gl";
import React from "react";
import { andThen, compose, max, min, path } from "ramda";
import { getCustomerData } from "../../utils/customer_utils";
import { layerNames } from "./layers/layer-config";
import poorSignalIcon from "./images/poor-signal-icon.png";
import moderateSignalIcon from "./images/moderate-signal-icon.png";
import strongSignalIcon from "./images/strong-signal-icon.png";
import lowBatteryIcon from "./images/low-battery-icon.png";
import emptyPipeIcon from "./images/empty-pipe-icon.png";
import highPressureIcon from "./images/high-pressure-icon.png";
import lowPressureIcon from "./images/low-pressure-icon.png";

const processClusterInfo = async (cluster) => {
  const { properties } = cluster;
  if (!properties) return null;

  switch (properties.type) {
    case layerNames.customers:
      const data = await getCustomerData(properties.id);
      return { ...data, type: properties.type };

    case layerNames.poor_signal_strength:
    case layerNames.moderate_signal_strength:
    case layerNames.strong_signal_strength:
    case layerNames.empty_pipe:
    case layerNames.low_battery:
    case layerNames.low_pressure:
    case layerNames.high_pressure: {
      const data = await getCustomerData(properties.id);
      return { ...data, type: "customers" };
    }

    case layerNames.bom:
    case layerNames.wasteWater:
    case layerNames.dams:
      return properties;

    default:
      return {};
  }
};

const MapCluster = ({
  type,
  cluster,
  superCluster,
  viewport,
  setViewport,
  onMouseIn,
  onMouseOut,
  onClick,
}) => {
  const [longitude, latitude] = cluster.geometry.coordinates;
  const {
    cluster: isCluster,
    point_count: pointCount,
    name,
  } = cluster.properties;

  const getBoundViewPort = (points) => {
    let topLeftLat = 1000;
    let topLeftLong = -1000;
    let bottomRightLat = -1000;
    let bottomRightLong = 1000;

    points.forEach((point) => {
      const coordinates = path(["geometry", "coordinates"], point);
      topLeftLat = min(topLeftLat, coordinates[1]);
      topLeftLong = max(topLeftLong, coordinates[0]);
      bottomRightLat = max(bottomRightLat, coordinates[1]);
      bottomRightLong = min(bottomRightLong, coordinates[0]);
    });

    return [
      [topLeftLong, topLeftLat],
      [bottomRightLong, bottomRightLat],
    ];
  };

  const fitClusterBound = (points) => {
    const { longitude, latitude, zoom } = new WebMercatorViewport(
      viewport
    ).fitBounds(getBoundViewPort(points));

    setViewport({
      ...viewport,
      longitude,
      latitude,
      zoom: zoom - 1,
      transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
      transitionDuration: "auto",
    });
  };

  const onMouseEnter = () => onMouseIn({ name, type, latitude, longitude });

  // ICON TYPE
  const isIcon = [
    "moderate_signal_strength",
    "strong_signal_strength",
    "poor_signal_strength",
    "empty_pipe",
    "low_battery",
    "low_pressure",
    "high_pressure",
  ].includes(type);

  let icon;
  switch (type) {
    case "poor_signal_strength":
      icon = poorSignalIcon;
      break;
    case "moderate_signal_strength":
      icon = moderateSignalIcon;
      break;
    case "strong_signal_strength":
      icon = strongSignalIcon;
      break;
    case "empty_pipe":
      icon = emptyPipeIcon;
      break;
    case "low_battery":
      icon = lowBatteryIcon;
      break;
    case "low_pressure":
      icon = lowPressureIcon;
      break;
    case "high_pressure":
      icon = highPressureIcon;
      break;
    default:
      icon = null;
  }

  // ✅ ✅ ICON-CLUSTER SPIDERFY WHEN ZOOM = MAX
  if (
    isCluster &&
    isIcon &&
    viewport.maxZoom === viewport.zoom &&
    pointCount > 1
  ) {
    const leaves = superCluster.getLeaves(cluster.id, Infinity);
    const R = 0.00008; // offset distance (~9m)

    return (
      <>
        {leaves.map((item, i) => {
          const angle = (i / leaves.length) * Math.PI * 2;
          const offsetLat = latitude + R * Math.cos(angle);
          const offsetLng = longitude + R * Math.sin(angle);

          return (
            <Marker
              key={item.properties.id}
              latitude={offsetLat}
              longitude={offsetLng}
            >
              <img
                src={icon}
                alt="child"
                onMouseEnter={() =>
                  onMouseIn({
                    ...item.properties,
                    latitude: offsetLat,
                    longitude: offsetLng,
                  })
                }
                onMouseLeave={onMouseOut}
                onClick={() => {
                  setViewport({
                    ...viewport,
                    longitude: offsetLng,
                    latitude: offsetLat,
                    transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
                    transitionDuration: "auto",
                  });
                  compose(andThen(onClick), processClusterInfo)(item);
                }}
                style={{
                  width: 25,
                  height: 25,
                  transform: "translate(-50%, -50%)",
                  cursor: "pointer",
                }}
              />
            </Marker>
          );
        })}
      </>
    );
  }

  // For icon cluster (normal)
  if (isCluster && isIcon) {
    const onMarkerClick = () =>
      fitClusterBound(superCluster.getLeaves(cluster.id, Infinity, 0));

    return (
      <Marker latitude={latitude} longitude={longitude}>
        <div
          className="cluster-marker"
          onClick={onMarkerClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseOut}
          style={{
            position: "relative",
            transform: "translate(-50%, -50%)",
            cursor: "pointer",
          }}
        >
          <img src={icon} alt="cluster" style={{ width: 40, height: 40 }} />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "12px",
              textShadow: "0 0 3px rgba(0,0,0,0.7)",
            }}
          >
            {pointCount}
          </div>
        </div>
      </Marker>
    );
  }

  // ❇️ Non-icon cluster
  if (isCluster && !isIcon) {
    if (viewport.maxZoom === viewport.zoom && pointCount > 1) {
      const clusters = superCluster.getChildren(cluster.id);

      const getLatLon = (i) => ({
        latitude: latitude + (8 * i) / 1000000,
        longitude: longitude + (8 * i) / 1000000,
      });

      return (
        <>
          {clusters.map((c, i) => {
            const latLon = getLatLon(i);
            return (
              <Marker key={i + ""} {...latLon}>
                <span
                  className={`map-marker ${type}`}
                  onMouseEnter={() =>
                    onMouseIn({ name: c.properties.name, type, ...latLon })
                  }
                  onMouseLeave={onMouseOut}
                  onClick={() => {
                    setViewport({
                      ...viewport,
                      transitionInterpolator: new FlyToInterpolator({
                        speed: 2,
                      }),
                      transitionDuration: "auto",
                      ...latLon,
                    });
                    compose(
                      andThen((_) => onClick({ ..._, ...latLon })),
                      processClusterInfo
                    )(c);
                  }}
                  style={{ transform: `translate(-50%, -50%)` }}
                />
              </Marker>
            );
          })}
        </>
      );
    } else {
      const onMarkerClick = () =>
        fitClusterBound(superCluster.getLeaves(cluster.id, Infinity, 0));

      return (
        <Marker latitude={latitude} longitude={longitude}>
          <div className={`map-marker ${type} cluster`} onClick={onMarkerClick}>
            {pointCount}
          </div>
        </Marker>
      );
    }
  }

  // ✅ NORMAL SINGLE MARKERS
  return (
    <Marker latitude={latitude} longitude={longitude}>
      {isIcon ? (
        <img
          src={icon}
          alt={type}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseOut}
          onClick={() => {
            setViewport({
              ...viewport,
              longitude,
              latitude,
              transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
              transitionDuration: "auto",
            });
            compose(andThen(onClick), processClusterInfo)(cluster);
          }}
          style={{
            width: 22,
            height: 22,
            transform: "translate(-50%, -50%)",
            cursor: "pointer",
          }}
        />
      ) : (
        <span
          className={`map-marker ${type}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseOut}
          onClick={() => {
            setViewport({
              ...viewport,
              longitude,
              latitude,
              transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
              transitionDuration: "auto",
            });
            compose(andThen(onClick), processClusterInfo)(cluster);
          }}
          style={{ transform: "translate(-50%, -50%)" }}
        />
      )}
    </Marker>
  );
};

export default MapCluster;
